import {
  FC,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import axios, { CancelTokenSource } from 'axios';
import moment from 'moment';
import debounce from 'lodash.debounce';
import xlsx from 'json-as-xlsx';

import DateRangeSelector from '../../../components/DateRangeSelector';
import ChannelMixChart from './ChannelMixChart';
import InfoTooltip from './InfoTooltip';
import { listStores, Store } from '../../plans';
import { CHANNELS } from '../constants/channels';
import { OTHER_CHANNEL_NAME } from '../constants/channelMix';
import {
  CUSTOM_CHANNEL_ID,
  EMPTY_GROUP,
  FILTER_FIELDS,
  FilterField,
  FilterGroup,
  applyChannelToGroups,
  dropEmptyFields,
  matchChannelId,
} from '../models/ChannelModel';
import {
  BreakdownKind,
  ChannelStageCounts,
  FUNNEL_STAGES,
  FunnelTotals,
  MetricRow,
  SessionRow,
  channelMixShare,
  computeChannelMix,
  computeMetricRows,
  conversionRate,
  formatPercent,
  isChurnMetric,
  stageConversion,
} from '../models/FunnelModel';
import { fetchAcquisitionFunnel } from '../services/AcquisitionFunnelCRUD';
import './AcquisitionFunnel.css';

const DEBOUNCE_MS = 1000;
const DEFAULT_DAYS_BACK = 29;
const MAX_RANGE_DAYS = 90;
const MAX_GROUPS = 20;
const SESSIONS_PAGE_SIZE = 200;
const DATE_PICKER_WIDTH_PX = 230;
const SELECT_MIN_WIDTH_PX = 200;
const UTM_INPUT_MIN_WIDTH_PX = 180;
const REGEX_INFO_TOOLTIP_WIDTH_PX = 320;
const SALES_ATTRIBUTION_INFO_WIDTH_PX = 360;
const SKELETON_COUNT_WIDTH = '50%';
const SKELETON_CONVERSION_WIDTH = '40%';

// El reparto de columnas lo manda el colgroup y no la primera fila: solo la
// tabla del embudo tiene cabecera, así que con anchos en las celdas cada tabla
// mediría por su cuenta y no cuadrarían entre sí.
const METRICS_COLGROUP = (
  <colgroup>
    <col />
    <col className="funnel-col-count" />
  </colgroup>
);

// Filas a cero para dibujar el esqueleto mientras carga: así la tabla no
// cambia de alto cuando llegan los datos.
const EMPTY_TOTALS: FunnelTotals = {
  sessions: 0,
  leads: 0,
  trials: 0,
  subscriptions: 0,
};
const SUMMARY_VALUE_MAX_LENGTH = 50;
const REGEX_DOCS_URL =
  'https://www.postgresql.org/docs/current/functions-matching.html#FUNCTIONS-POSIX-REGEXP';

const FIELD_I18N_KEY: Record<FilterField, string> = {
  source: 'marketingDashboard.utmSource',
  medium: 'marketingDashboard.utmMedium',
  campaign: 'marketingDashboard.utmCampaign',
  term: 'marketingDashboard.utmTerm',
  content: 'marketingDashboard.utmContent',
  referrer: 'marketingDashboard.referrer',
  gclid: 'marketingDashboard.gclid',
};

// Nombre de cada desglose: cabecera en pantalla cuando una fila tiene varios,
// y columna «Grupo» en el Excel.
const BREAKDOWN_TITLE_KEY: Record<BreakdownKind, string> = {
  salesAttribution: 'marketingDashboard.salesAttributionTitle',
  planState: 'marketingDashboard.planStateTitle',
  reason: 'marketingDashboard.reasonTitle',
  leadSource: 'marketingDashboard.leadSourceTitle',
};

// Desgloses con etiqueta traducida desde un mapa fijo de buckets.
const BUCKET_I18N_KEY: Partial<Record<BreakdownKind, Record<string, string>>> =
  {
    salesAttribution: {
      agent: 'marketingDashboard.salesAttributionAgent',
      atc: 'marketingDashboard.salesAttributionAtc',
      direct: 'marketingDashboard.salesAttributionDirect',
      unassigned: 'marketingDashboard.salesAttributionUnclassified',
    },
    leadSource: {
      'call-request': 'marketingDashboard.sourceCallRequest',
      'vixiees-inbound': 'marketingDashboard.sourceVixiees',
    },
  };

function formatDate(d: Date): string {
  return moment(d).format('YYYY-MM-DD');
}

const AcquisitionFunnel: FC = () => {
  const intl = useIntl();

  const [since, setSince] = useState<Date>(
    moment().subtract(DEFAULT_DAYS_BACK, 'days').toDate()
  );
  const [until, setUntil] = useState<Date>(moment().toDate());
  const [storeId, setStoreId] = useState<number | null>(null);
  const [groups, setGroups] = useState<FilterGroup[]>([{ ...EMPTY_GROUP }]);
  const [expanded, setExpanded] = useState<boolean[]>([true]);
  const [isUserCustomized, setIsUserCustomized] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [page, setPage] = useState(1);
  const [expandedPetRows, setExpandedPetRows] = useState<
    Record<number, boolean>
  >({});

  const matchedChannelId = matchChannelId(groups, CHANNELS);
  const channelId =
    isUserCustomized || matchedChannelId === null
      ? CUSTOM_CHANNEL_ID
      : matchedChannelId;
  const [totals, setTotals] = useState<FunnelTotals | null>(null);
  // Reparto por canal ya completo para los parámetros actuales, para el Excel.
  const [channelMix, setChannelMix] = useState<ChannelStageCounts[] | null>(
    null
  );
  // Cada etapa despliega su propio reparto por estado, igual que las bajas
  // despliegan sus motivos.
  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>(
    {}
  );
  const [error, setError] = useState<ReactNode>(null);
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef<CancelTokenSource | null>(null);

  const quickRanges = [
    {
      label: intl.formatMessage({ id: 'marketingDashboard.currentMonth' }),
      since: moment().startOf('month').toDate(),
      until: moment().toDate(),
    },
    {
      label: intl.formatMessage({ id: 'marketingDashboard.previousMonth' }),
      since: moment().subtract(1, 'month').startOf('month').toDate(),
      until: moment().subtract(1, 'month').endOf('month').toDate(),
    },
  ];

  const rangeDays = moment(until).diff(moment(since), 'days') + 1;
  const rangeError =
    rangeDays > MAX_RANGE_DAYS
      ? intl.formatMessage({ id: 'marketingDashboard.rangeTooLong' })
      : null;

  useEffect(() => {
    async function fetchStores() {
      try {
        const { data } = await listStores();
        setStores(data);
        const esStore = data.find((s) => s.country === 'ES');
        if (esStore) setStoreId(esStore.id);
      } catch {
        // Stores not loaded — dropdown stays with only "Todas las tiendas".
      }
    }
    fetchStores();
  }, []);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (params: Parameters<typeof fetchAcquisitionFunnel>[0]) => {
        // Cancel any request still in flight so a slow earlier response can't
        // overwrite a newer one (race condition). axios 0.21 predates
        // AbortController, so use its CancelToken.
        cancelRef.current?.cancel();
        const source = axios.CancelToken.source();
        cancelRef.current = source;

        setLoading(true);
        try {
          const data = await fetchAcquisitionFunnel(params, source.token);
          if (cancelRef.current !== source) return; // superseded
          setTotals(data);
          setError(null);
        } catch (err: any) {
          // Ignore the request we deliberately cancelled / superseded.
          if (cancelRef.current !== source || axios.isCancel(err)) return;
          const status = err?.response?.status;
          // The funnel query has a fixed server-side statement timeout (~20s);
          // when it trips, Django returns HTTP 500. Treat any 500 from this
          // read-only endpoint as a timeout (alongside the proxy/client cases).
          const isTimeout =
            err?.code === 'ECONNABORTED' ||
            status === 408 ||
            status === 500 ||
            status === 504;
          const detail = isTimeout
            ? intl.formatMessage(
                { id: 'marketingDashboard.timeoutError' },
                { strong: (chunks) => <strong>{chunks}</strong> }
              )
            : (err?.response?.data?.detail ??
              intl.formatMessage({ id: 'marketingDashboard.fetchError' }));
          setError(detail);
        } finally {
          if (cancelRef.current === source) setLoading(false);
        }
      }, DEBOUNCE_MS),
    [intl]
  );

  useEffect(() => {
    setPage(1);
  }, [since, until, storeId, groups]);

  // Collapse every pets cell whenever a new page/filter result arrives, since
  // row indices are reused across pages.
  useEffect(() => {
    setExpandedPetRows({});
  }, [totals]);

  useEffect(() => {
    if (rangeError) {
      debouncedFetch.cancel();
      return;
    }
    debouncedFetch({
      since: formatDate(since),
      until: formatDate(until),
      store_id: storeId,
      groups: groups.map(dropEmptyFields),
      page,
      page_size: SESSIONS_PAGE_SIZE,
    });
    return () => debouncedFetch.cancel();
  }, [since, until, storeId, groups, page, debouncedFetch, rangeError]);

  // Cancel any in-flight request on unmount.
  useEffect(
    () => () => {
      cancelRef.current?.cancel();
      cancelRef.current = null;
    },
    []
  );

  const rows = useMemo(
    () => (totals ? computeMetricRows(totals) : null),
    [totals]
  );

  function onChannelChange(id: string) {
    if (id === CUSTOM_CHANNEL_ID) {
      setGroups([{ ...EMPTY_GROUP }]);
      setExpanded([true]);
      setIsUserCustomized(true);
      return;
    }
    const channel = CHANNELS.find((c) => c.name === id) ?? null;
    const newGroups = applyChannelToGroups(channel);
    setGroups(newGroups);
    const allCollapsed = id !== '' && newGroups.length > 0;
    setExpanded(newGroups.map(() => !allCollapsed));
    setIsUserCustomized(false);
  }

  function onGroupFieldChange(
    groupIndex: number,
    field: FilterField,
    value: string
  ) {
    setGroups((prev) =>
      prev.map((g, i) => (i === groupIndex ? { ...g, [field]: value } : g))
    );
    setIsUserCustomized(true);
  }

  function onAddGroup() {
    if (groups.length >= MAX_GROUPS) return;
    setGroups((prev) => [...prev, { ...EMPTY_GROUP }]);
    setExpanded((prev) => [...prev, true]);
    setIsUserCustomized(true);
  }

  function onRemoveGroup(index: number) {
    if (groups.length <= 1) return;
    setGroups((prev) => prev.filter((_, i) => i !== index));
    setExpanded((prev) => prev.filter((_, i) => i !== index));
    setIsUserCustomized(true);
  }

  function onToggleExpanded(index: number) {
    setExpanded((prev) => prev.map((v, i) => (i === index ? !v : v)));
  }

  function togglePetRow(rowIndex: number) {
    setExpandedPetRows((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  }

  function summarizeGroup(group: FilterGroup): string {
    const parts: string[] = [];
    for (const field of FILTER_FIELDS) {
      const value = group[field];
      if (!value) continue;
      const label = intl.formatMessage({ id: FIELD_I18N_KEY[field] });
      const displayed =
        value.length > SUMMARY_VALUE_MAX_LENGTH
          ? `${value.slice(0, SUMMARY_VALUE_MAX_LENGTH)}…`
          : value;
      parts.push(`${label}: ${displayed}`);
    }
    return parts.length === 0
      ? intl.formatMessage({ id: 'marketingDashboard.emptyFilterSummary' })
      : parts.join(' · ');
  }

  const metricKey: Record<string, string> = {
    sessions: 'marketingDashboard.stageSessions',
    preLeads: 'marketingDashboard.stagePreLeads',
    leads: 'marketingDashboard.stageLeads',
    trials: 'marketingDashboard.stageTrials',
    subscriptions: 'marketingDashboard.stageSubscribers',
    pauses: 'marketingDashboard.pauses',
  };

  const metricInfoKey: Record<string, string> = {
    sessions: 'marketingDashboard.infoSessions',
    preLeads: 'marketingDashboard.infoPreLeads',
    leads: 'marketingDashboard.infoLeads',
    trials: 'marketingDashboard.infoTrials',
    subscriptions: 'marketingDashboard.infoSubscribers',
    pauses: 'marketingDashboard.pausesInfo',
  };

  // Los estados de plan, el reparto de ventas y el origen de los pre-leads
  // tienen traducción; los códigos de baja van en crudo. Igual en pantalla que
  // en el Excel.
  function breakdownLabel(kind: BreakdownKind, bucket: string): string {
    if (kind === 'planState') {
      return intl.formatMessage({
        id: `planState.${bucket}`,
        defaultMessage: bucket,
      });
    }
    const bucketKeys = BUCKET_I18N_KEY[kind];
    if (bucketKeys) {
      const key = bucketKeys[bucket];
      return key ? intl.formatMessage({ id: key }) : bucket;
    }
    return bucket || intl.formatMessage({ id: 'marketingDashboard.noReason' });
  }

  function renderPets(session: SessionRow) {
    if (session.pets.length === 0) return '—';
    const lines: { key: string; text: string; active: boolean | null }[] = [];
    session.pets.forEach((pet, petIdx) => {
      if (pet.plans.length === 0) {
        lines.push({
          key: `${pet.id}-${petIdx}`,
          text: intl.formatMessage(
            { id: 'marketingDashboard.petNoPlan' },
            { name: pet.name }
          ),
          active: null,
        });
        return;
      }
      pet.plans.forEach((plan, planIdx) => {
        lines.push({
          key: `${pet.id}-${petIdx}-${planIdx}`,
          text: intl.formatMessage(
            { id: 'marketingDashboard.petWithPlan' },
            { name: pet.name, code: plan.code, orders: plan.paid_orders }
          ),
          active: plan.is_active,
        });
      });
    });
    return (
      <div className="d-flex flex-column">
        {lines.map((line) => (
          <span
            key={line.key}
            className={`text-nowrap ${
              line.active === null
                ? 'text-muted'
                : line.active
                  ? 'text-success'
                  : 'text-gray-700'
            }`}
          >
            {line.text}
          </span>
        ))}
      </div>
    );
  }

  const sessionsList = totals?.sessions_list ?? null;
  const totalPages = sessionsList
    ? Math.max(1, Math.ceil(sessionsList.count / sessionsList.page_size))
    : 1;

  const canDownload =
    !loading && rows !== null && totals !== null && totals.sessions > 0;

  function downloadXlsx() {
    if (!rows) return;
    const t = (id: string) => intl.formatMessage({ id });
    const metricLabel = (row: MetricRow) =>
      intl.formatMessage({ id: metricKey[row.id] });

    const metricsSheet = {
      sheet: t('marketingDashboard.title'),
      columns: [
        { label: t('marketingDashboard.metricHeader'), value: 'metric' },
        { label: t('marketingDashboard.countHeader'), value: 'count' },
        {
          label: t('marketingDashboard.conversionHeader'),
          value: 'conversion',
        },
      ],
      content: rows.map((row) => ({
        metric: metricLabel(row),
        count: row.count,
        conversion: formatPercent(row.stepConversion),
      })),
    };

    // Una fila por métrica y entrada del desglose, igual que los desplegables.
    // El export lleva siempre el desglose entero, esté o no abierto en pantalla.
    const breakdownSheet = {
      sheet: t('marketingDashboard.breakdownTitle'),
      columns: [
        { label: t('marketingDashboard.metricHeader'), value: 'metric' },
        { label: t('marketingDashboard.breakdownGroupHeader'), value: 'group' },
        { label: t('marketingDashboard.breakdownHeader'), value: 'bucket' },
        { label: t('marketingDashboard.countHeader'), value: 'count' },
      ],
      content: rows.flatMap((row) =>
        row.breakdowns.flatMap(({ kind, entries }) =>
          entries.map(([bucket, count]) => ({
            metric: metricLabel(row),
            group: t(BREAKDOWN_TITLE_KEY[kind]),
            bucket: breakdownLabel(kind, bucket),
            count,
          }))
        )
      ),
    };

    // El reparto por canal solo va si ha terminado de calcularse con estos
    // parámetros; la gráfica lo vacía en cuanto cambian.
    const mixSheets = [];
    if (channelId === '' && channelMix && totals) {
      const mix = computeChannelMix(channelMix, totals);
      // Todos los canales por sesiones de mayor a menor; Otro es lo que no
      // reclama ninguno de los demás.
      const mixRows = [
        ...mix.channels,
        { name: OTHER_CHANNEL_NAME, counts: mix.remainder },
      ].sort((a, b) => b.counts.sessions - a.counts.sessions);
      mixSheets.push({
        sheet: t('marketingDashboard.channelMixTitle'),
        columns: [
          {
            label: t('marketingDashboard.channelMixChannelHeader'),
            value: 'channel',
          },
          // Sesiones abre el embudo: es la única etapa sin conversión.
          ...FUNNEL_STAGES.flatMap((stage, index) => [
            { label: t(metricKey[stage]), value: stage },
            { label: `${t(metricKey[stage])} (%)`, value: `${stage}Share` },
            ...(index > 0
              ? [
                  {
                    label: `${t(metricKey[stage])} (${t('marketingDashboard.conversionHeader')})`,
                    value: `${stage}Conversion`,
                  },
                ]
              : []),
          ]),
        ],
        content: [
          ...mixRows.map(({ name, counts }) => {
            const row: Record<string, string | number> = { channel: name };
            for (const stage of FUNNEL_STAGES) {
              row[stage] = counts[stage];
              row[`${stage}Share`] = formatPercent(
                channelMixShare(mix, counts[stage], stage)
              );
              row[`${stage}Conversion`] = formatPercent(
                stageConversion(counts, stage)
              );
            }
            return row;
          }),
          ...mix.failedNames.map((name) => ({
            channel: `${name} (${t('marketingDashboard.channelMixNotCalculated')})`,
          })),
        ],
      });
    }

    xlsx([metricsSheet, breakdownSheet, ...mixSheets] as any, {
      fileName: `embudo-conversion-${formatDate(since)}-${formatDate(until)}`,
      extraLength: 0,
      writeMode: 'writeFile',
      writeOptions: {},
    });
  }

  const metricRows = rows ?? computeMetricRows(EMPTY_TOTALS);
  const funnelRows = metricRows.filter((row) => !isChurnMetric(row.id));
  const churnRows = metricRows.filter((row) => isChurnMetric(row.id));

  function renderMetricRow(row: MetricRow) {
    const isOpen = !!expandedStages[row.id];
    const canOpen = !loading && row.breakdowns.length > 0;
    // Con un solo desglose la métrica ya dice qué es; con varios, cada uno
    // lleva su cabecera.
    const hasGroups = row.breakdowns.length > 1;
    const label = intl.formatMessage({ id: metricKey[row.id] });
    return (
      <Fragment key={row.id}>
        <tr>
          <td>
            {canOpen ? (
              <button
                type="button"
                className="funnel-row-label"
                onClick={() =>
                  setExpandedStages((prev) => ({
                    ...prev,
                    [row.id]: !prev[row.id],
                  }))
                }
                aria-expanded={isOpen}
              >
                <i
                  className={`fas fa-chevron-${
                    isOpen ? 'down' : 'right'
                  } funnel-row-caret fs-8`}
                />
                {label}
              </button>
            ) : (
              <span className="funnel-row-label">{label}</span>
            )}
            <InfoTooltip
              text={intl.formatMessage({ id: metricInfoKey[row.id] })}
            />
          </td>
          <td>
            {loading || !rows ? (
              <div className="d-flex flex-column">
                <span
                  className="funnel-skeleton"
                  style={{ width: SKELETON_COUNT_WIDTH }}
                />
                <span
                  className="funnel-skeleton mt-1"
                  style={{ width: SKELETON_CONVERSION_WIDTH }}
                />
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span>{row.count}</span>
                {/* Siempre presente, aunque la fila no tenga conversión:
                    mantiene todas las filas a la misma altura. */}
                <div className="fs-7 text-muted">
                  {formatPercent(row.stepConversion) || '\u00A0'}
                </div>
              </div>
            )}
          </td>
        </tr>
        {isOpen &&
          row.breakdowns.map(({ kind, entries }) => (
            <Fragment key={kind}>
              {hasGroups && (
                <tr className="funnel-row-child">
                  <td colSpan={2}>
                    <span className="funnel-row-child-label funnel-row-group-label">
                      {intl.formatMessage({ id: BREAKDOWN_TITLE_KEY[kind] })}
                    </span>
                    {kind === 'salesAttribution' && (
                      <InfoTooltip
                        widthPx={SALES_ATTRIBUTION_INFO_WIDTH_PX}
                        text={intl.formatMessage({
                          id: 'marketingDashboard.salesAttributionInfo',
                        })}
                      />
                    )}
                  </td>
                </tr>
              )}
              {entries.map(([bucket, count]) => (
                <tr
                  key={`${row.id}-${kind}-${bucket}`}
                  className="funnel-row-child"
                >
                  <td>
                    <span className="funnel-row-child-label">
                      {breakdownLabel(kind, bucket)}
                    </span>
                  </td>
                  <td>
                    {/* El reparto de ventas suma justo Trials: cada parte
                        lleva su peso sobre el total, como las conversiones. */}
                    {kind === 'salesAttribution' ? (
                      <div className="d-flex flex-column">
                        <span>{count}</span>
                        <div className="fs-7 text-muted">
                          {formatPercent(conversionRate(count, row.count))}
                        </div>
                      </div>
                    ) : (
                      count
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
      </Fragment>
    );
  }

  return (
    <div className="card shadow-sm p-10">
      <div className="d-flex flex-wrap gap-6 mb-6">
        <div style={{ width: `${DATE_PICKER_WIDTH_PX}px` }}>
          <DateRangeSelector
            initialSince={since}
            initialUntil={until}
            quickRanges={quickRanges}
            onSelected={(s, u) => {
              setSince(s);
              setUntil(u);
            }}
            label={intl.formatMessage({ id: 'marketingDashboard.dateRange' })}
            error={!!rangeError}
          />
          {rangeError && (
            <div className="text-danger mt-1 fs-7">{rangeError}</div>
          )}
        </div>

        <div style={{ minWidth: `${SELECT_MIN_WIDTH_PX}px` }}>
          <label className="form-label">
            {intl.formatMessage({ id: 'marketingDashboard.store' })}
          </label>
          <select
            className="form-select"
            value={storeId ?? ''}
            onChange={(e) =>
              setStoreId(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <option value="">
              {intl.formatMessage({ id: 'marketingDashboard.allStores' })}
            </option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ minWidth: `${SELECT_MIN_WIDTH_PX}px` }}>
          <label className="form-label">
            {intl.formatMessage({ id: 'marketingDashboard.channel' })}
          </label>
          <select
            className="form-select"
            value={channelId}
            onChange={(e) => onChannelChange(e.target.value)}
          >
            <option value="">
              {intl.formatMessage({ id: 'marketingDashboard.allChannels' })}
            </option>
            <option value={CUSTOM_CHANNEL_ID}>
              {intl.formatMessage({ id: 'marketingDashboard.customChannel' })}
            </option>
            {CHANNELS.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="d-flex align-self-end mb-5 ms-2">
            <span
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              aria-label={intl.formatMessage({
                id: 'marketingDashboard.loading',
              })}
            />
          </div>
        )}
      </div>

      <div className="d-flex align-items-center mb-3">
        <span className="fs-6 fw-bold">
          {intl.formatMessage({ id: 'marketingDashboard.utmTitle' })}
        </span>
        <InfoTooltip widthPx={REGEX_INFO_TOOLTIP_WIDTH_PX}>
          {intl.formatMessage({ id: 'marketingDashboard.regexInfo' })}{' '}
          <a href={REGEX_DOCS_URL} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage({ id: 'marketingDashboard.regexInfoLink' })}
          </a>
        </InfoTooltip>
        <button
          type="button"
          className="btn btn-sm btn-light-primary ms-auto"
          disabled={groups.length >= MAX_GROUPS}
          onClick={onAddGroup}
        >
          <i className="fa fa-plus me-1" />
          {intl.formatMessage({ id: 'marketingDashboard.addFilter' })}
        </button>
      </div>

      <div className="d-flex flex-column gap-3 mb-6">
        {groups.map((group, filterIndex) => (
          <Fragment key={filterIndex}>
            {filterIndex > 0 && (
              <div
                className="position-relative opacity-50 my-2"
                role="separator"
              >
                <hr className="m-0 " />
                <span className="position-absolute top-50 start-50 translate-middle px-3 fs-7 text-uppercase fw-bold text-muted bg-body">
                  {intl.formatMessage({
                    id: 'marketingDashboard.orSeparator',
                  })}
                </span>
              </div>
            )}
            <div className="card border">
              <div
                role="button"
                tabIndex={0}
                className={`card-body ${
                  expanded[filterIndex] ? 'pt-2 pb-1 px-3' : 'p-3'
                } d-flex align-items-center gap-3`}
                onClick={() => onToggleExpanded(filterIndex)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleExpanded(filterIndex);
                  }
                }}
              >
                {!expanded[filterIndex] && (
                  <div className="flex-grow-1 fs-7 text-truncate">
                    <strong>
                      {intl.formatMessage({
                        id: 'marketingDashboard.filteringBy',
                      })}
                    </strong>{' '}
                    {summarizeGroup(group)}
                  </div>
                )}
                {expanded[filterIndex] && (
                  <span className="flex-grow-1" aria-hidden="true" />
                )}
                {groups.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-icon position-absolute top-0 end-0 me-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveGroup(filterIndex);
                    }}
                    aria-label={intl.formatMessage({
                      id: 'marketingDashboard.removeFilter',
                    })}
                  >
                    <i className="fa fa-times" />
                  </button>
                )}
                <i
                  className={`fa fa-chevron-${expanded[filterIndex] ? 'up' : 'down'} ${expanded[filterIndex] && 'mt-2'} text-muted`}
                />
              </div>
              {expanded[filterIndex] && (
                <div className="card-body pt-0 px-5 pb-5">
                  <div className="d-flex flex-wrap gap-4">
                    {FILTER_FIELDS.map((field) => (
                      <div
                        key={field}
                        style={{
                          minWidth: `${UTM_INPUT_MIN_WIDTH_PX}px`,
                          flex: 1,
                        }}
                      >
                        <label className="form-label">
                          {intl.formatMessage({ id: FIELD_I18N_KEY[field] })}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={group[field] ?? ''}
                          onChange={(e) =>
                            onGroupFieldChange(
                              filterIndex,
                              field,
                              e.target.value
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Fragment>
        ))}
      </div>

      {error && !loading && (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ width: 'fit-content' }}
        >
          <span className="me-2" aria-hidden="true">
            😢
          </span>
          {error}
        </div>
      )}

      {!error && !loading && totals !== null && totals.sessions === 0 && (
        <div
          className="alert alert-info"
          role="status"
          style={{ width: 'fit-content' }}
        >
          {intl.formatMessage(
            { id: 'marketingDashboard.noSessions' },
            {
              since: moment(since).format('DD/MM/YYYY'),
              until: moment(until).format('DD/MM/YYYY'),
            }
          )}
        </div>
      )}

      {!error && (loading || (totals !== null && totals.sessions > 0)) && (
        <>
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={downloadXlsx}
              disabled={!canDownload}
            >
              <i className="fas fa-file-excel me-2" />
              {intl.formatMessage({ id: 'marketingDashboard.downloadXlsx' })}
            </button>
          </div>
          <div className="card sessions-card-inset overflow-hidden">
            <div className="card-body p-0">
              <table className="table table-striped table-row-bordered gs-2 mb-0 funnel-metrics-table">
                {METRICS_COLGROUP}
                <thead className="fw-bolder">
                  <tr>
                    <th>
                      {intl.formatMessage({
                        id: 'marketingDashboard.metricHeader',
                      })}
                    </th>
                    <th>
                      {intl.formatMessage({
                        id: 'marketingDashboard.countHeader',
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>{funnelRows.map(renderMetricRow)}</tbody>
              </table>
            </div>
          </div>

          {/* Las bajas van en su propia tabla: no encadenan con el embudo y no
              deben leerse como un paso más. */}
          <div className="card sessions-card-inset overflow-hidden funnel-churn-card">
            <div className="card-body p-0">
              <table className="table table-striped table-row-bordered gs-2 mb-0 funnel-metrics-table">
                <caption className="visually-hidden">
                  {intl.formatMessage({ id: 'marketingDashboard.churnTitle' })}
                </caption>
                {METRICS_COLGROUP}
                <tbody>{churnRows.map(renderMetricRow)}</tbody>
              </table>
            </div>
          </div>

          {channelId === '' && !rangeError && totals !== null && (
            <ChannelMixChart
              since={formatDate(since)}
              until={formatDate(until)}
              storeId={storeId}
              total={totals}
              mainLoading={loading}
              onCalculated={setChannelMix}
            />
          )}

          {!loading && sessionsList && sessionsList.results.length > 0 && (
            <div className="mt-10">
              <div className="text-muted mb-3">
                {intl.formatMessage(
                  { id: 'marketingDashboard.sessionsTableTitle' },
                  {
                    count: sessionsList.count,
                    pageSize: sessionsList.page_size,
                    br: () => <br />,
                  }
                )}
              </div>
              <div className="card sessions-card-inset overflow-hidden">
                <div className="card-body p-0">
                  <div className="table-responsive sessions-table-scroll">
                    <table className="table table-striped table-row-bordered gs-2 mb-0">
                      <thead className="fw-bolder">
                        <tr>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.colSessionStart',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.colName',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.colEmail',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.colPhone',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.colPets',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.utmSource',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.utmMedium',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.utmCampaign',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.utmTerm',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.utmContent',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.referrer',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'marketingDashboard.gclid',
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessionsList.results.map((session, idx) => (
                          <tr key={idx}>
                            <td className="text-nowrap">
                              {moment(session.session_started_at).format(
                                'DD/MM/YYYY HH:mm'
                              )}
                            </td>
                            <td className="text-nowrap">
                              {session.name ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.email ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.phone ?? '—'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-decoration-none text-nowrap"
                                onClick={() => togglePetRow(idx)}
                              >
                                {intl.formatMessage(
                                  { id: 'marketingDashboard.petsToggle' },
                                  { count: session.pets.length }
                                )}
                                <i
                                  className={`fa fa-chevron-${
                                    expandedPetRows[idx] ? 'up' : 'down'
                                  } ms-2`}
                                />
                              </button>
                              {expandedPetRows[idx] && (
                                <div className="mt-2">
                                  {renderPets(session)}
                                </div>
                              )}
                            </td>
                            <td className="text-nowrap">
                              {session.utm_source ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.utm_medium ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.utm_campaign ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.utm_term ?? '—'}
                            </td>
                            <td className="text-nowrap">
                              {session.utm_content ?? '—'}
                            </td>
                            <td>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: 220 }}
                                title={session.referrer ?? undefined}
                              >
                                {session.referrer ?? '—'}
                              </div>
                            </td>
                            <td>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: 160 }}
                                title={session.gclid ?? undefined}
                              >
                                {session.gclid ?? '—'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card-footer d-flex justify-content-center align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {intl.formatMessage({ id: 'marketingDashboard.prevPage' })}
                  </button>
                  <span>
                    {intl.formatMessage(
                      { id: 'marketingDashboard.pageIndicator' },
                      { page, pages: totalPages }
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {intl.formatMessage({ id: 'marketingDashboard.nextPage' })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AcquisitionFunnel;
